import type { ReactNode } from "react";
import { TrendingUp, TrendingDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

type StatCardColor = "blue" | "green" | "amber" | "red" | "purple";

type StatCardProps = {
  title: string;
  value: string | number;
  change?: number;
  changeLabel?: string;
  icon?: ReactNode;
  color?: StatCardColor;
  loading?: boolean;
};

const colorMap: Record<StatCardColor, string> = {
  blue:   "bg-blue-100 text-blue-600",
  green:  "bg-green-100 text-green-600",
  amber:  "bg-amber-100 text-amber-600",
  red:    "bg-red-100 text-red-600",
  purple: "bg-purple-100 text-purple-600",
};

export function StatCard({
  title,
  value,
  change,
  changeLabel,
  icon,
  color = "blue",
  loading = false,
}: StatCardProps) {
  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="space-y-2 flex-1">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-8 w-16" />
              <Skeleton className="h-3 w-32" />
            </div>
            <Skeleton className="h-10 w-10 rounded-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const isPositive = change !== undefined && change > 0;
  const isNegative = change !== undefined && change < 0;
  const absChange = change !== undefined ? Math.abs(change) : 0;

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="space-y-1 flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-500 truncate">{title}</p>
            <p className="text-3xl font-bold text-gray-900 tracking-tight">
              {value}
            </p>
            {change !== undefined && (
              <div className="flex items-center gap-1 pt-0.5">
                {isPositive && (
                  <TrendingUp className="h-3.5 w-3.5 text-green-500 shrink-0" />
                )}
                {isNegative && (
                  <TrendingDown className="h-3.5 w-3.5 text-red-500 shrink-0" />
                )}
                <span
                  className={cn(
                    "text-xs font-medium",
                    isPositive && "text-green-600",
                    isNegative && "text-red-600",
                    !isPositive && !isNegative && "text-gray-500",
                  )}
                >
                  {isPositive ? "+" : ""}{absChange}%
                  {changeLabel && (
                    <span className="text-gray-400 font-normal ml-1 truncate">{changeLabel}</span>
                  )}
                </span>
              </div>
            )}
          </div>
          {icon && (
            <div
              className={cn(
                "h-10 w-10 rounded-full flex items-center justify-center shrink-0 ml-4",
                colorMap[color],
              )}
            >
              {icon}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
